// @ts-nocheck
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, X } from 'lucide-react';
import { useEffectiveAuth } from '@/hooks/useEffectiveAuth';
import { Button } from '@/components/ui/button';

interface ScoutConnectionNotification {
  id: string;
  title: string;
  message: string;
  created_at: string;
  read: boolean;
}

const ScoutConnectionAlert: React.FC = () => {
  const { profile, user } = useEffectiveAuth();
  
  console.log('🔔 ScoutConnectionAlert - Debug info:', {
    profileId: profile?.id,
    userId: user?.id,
    activeProfileId: localStorage.getItem('activeProfileId'),
    isImpersonating: !!localStorage.getItem('activeProfileId')
  });

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['scout-connection-notifications', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('notifications')
        .select('id, title, message, created_at, read')
        .eq('user_id', user.id)
        .eq('type', 'scout_connection')
        .eq('read', false)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching scout connection notifications:', error);
        return [];
      }

      console.log('Scout connection notifications:', data);
      return data as ScoutConnectionNotification[];
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const markAsRead = async (notificationId: string) => {
    if (!user?.id) return;
    
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId)
      .eq('user_id', user.id);
  };

  const markAllAsRead = async () => {
    if (!user?.id || notifications.length === 0) return;
    
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('type', 'scout_connection')
      .eq('read', false);
  };

  if (isLoading || notifications.length === 0) {
    return null;
  }

  return (
    <Card className="w-full border-blue-200 bg-blue-50 hover:border-blue-300 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-blue-100 text-blue-600">
            <Users size={22} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-medium text-lg text-blue-900">Scout Connections</h3>
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                {notifications.length} new
              </Badge>
            </div>
            
            <div className="space-y-2 mb-3">
              {notifications.slice(0, 3).map((notification) => (
                <div key={notification.id} className="flex items-center justify-between">
                  <p className="text-blue-800 text-sm">
                    {notification.message}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => markAsRead(notification.id)}
                    className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
                  >
                    <X size={12} />
                  </Button>
                </div>
              ))}
              
              {notifications.length > 3 && (
                <p className="text-xs text-blue-600">
                  +{notifications.length - 3} more scout connections
                </p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={markAllAsRead}
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
              >
                Mark all as read
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ScoutConnectionAlert;